<script lang="ts">
  export interface GlobalSearchInputProps {
    /** The default search context to use when no context is selected. */
    defaultContext?: SearchContextType
    class?: HTMLAttributes['class']
    inputClass?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { ref, useTemplateRef, watch, type HTMLAttributes } from 'vue'
  import { GatTextEdit, cn } from '@gat-ship/vue-components'
  import { useSearch, useRecentSearch } from '../../utils/search-providers'
  import { useRouter } from '@gat-ship/lib/router'
  import {
    searchContextItems,
    searchContextTypesMapping,
    type SearchContextType
  } from '../../utils/global-search-utils'
  import type { SearchResult } from '../../utils/search-types'

  const props = withDefaults(defineProps<GlobalSearchInputProps>(), {
    defaultContext: () => searchContextItems.global
  })

  const emit = defineEmits<{
    (e: 'item-selected', item: SearchResult): void
    (e: 'focus', event: FocusEvent): void
    (e: 'blur', event: FocusEvent): void
  }>()

  const _searchInputRef = useTemplateRef('searchInputRef')

  const {
    defaultSearchContext,
    callSearch,
    searchInputValue: searchInput,
    searchInputRef,
    isSearchInputEmpty: isSearchEmpty,
    selectedSearchContext,
    setSelectedSearchContext,
    isSearching,
    clearSearchResults
  } = useSearch()

  const { addRecentSearch } = useRecentSearch()

  watch(selectedSearchContext, (newContext) => {
    if (isSearchEmpty.value) {
      return
    }
    callSearch(searchInput.value, newContext)
  })

  const router = useRouter()

  function onEnterPressed(search: string) {
    const contextType = selectedSearchContext.value
    if (isSearchEmpty.value) {
      return
    }
    if (selectedSearchContext.value === defaultSearchContext) {
      addRecentSearch({
        searchString: search,
        context: contextType,
        timestamp: Date.now()
      })
      callSearch(search, contextType)
    }
  }

  function onUpdate(search: string | undefined | null) {
    clearSearchResults()
    const context = selectedSearchContext.value
    if (!search) {
      return
    }
    Object.values(searchContextItems).forEach((context) => {
      const metadata = searchContextTypesMapping[context]
      const prefix = `${metadata.prefix}:`
      if (search.toLowerCase().startsWith(prefix.toLowerCase())) {
        setSelectedSearchContext(context)
        searchInput.value = search.slice(prefix.length)
        return
      }
    })
    if (context === defaultSearchContext) {
      // We do not call the api on keydown events for non-default contexts
      // The user must press enter to call the api in the default context
      return
    }
    callSearch(search, selectedSearchContext.value)
  }

  function onKeyDownDown(event: KeyboardEvent) {
    event.preventDefault()
  }
  function onKeyDownBackspace(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement | null
    const caretAtStart = input?.selectionStart === 0
    if (isSearchEmpty.value || caretAtStart) {
      setSelectedSearchContext(props.defaultContext)
      return
    }
  }
</script>

<template>
  <div
    :class="
      cn(
        'border-input flex w-full items-center gap-1 border-b p-0.5',
        props.class
      )
    "
  >
    <div class="flex items-center gap-1 pl-2">
      <gat-icon icon="Search" class="size-4" />
      <badge
        variant="outline"
        v-if="selectedSearchContext !== props.defaultContext"
      >
        {{ searchContextTypesMapping[selectedSearchContext].prefix }}:
      </badge>
    </div>
    <gat-text-edit
      ref="searchInputRef"
      @vue:mounted="() => (searchInputRef = _searchInputRef)"
      v-model="searchInput"
      default-value=""
      :clearable="false"
      :loading="isSearching"
      class="w-full"
      :input-class="
        cn('focus-visible:ring-0 border-none px-0', props.inputClass)
      "
      @keydown.enter="onEnterPressed(searchInput)"
      @keydown.down="onKeyDownDown($event)"
      @keydown.backspace="onKeyDownBackspace($event)"
      @update:model-value="onUpdate($event)"
      @click:clear="[
        clearSearchResults(),
        setSelectedSearchContext(props.defaultContext),
        searchInputRef?.focus()
      ]"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    >
      <template
        #append-inner
        v-if="searchInput || selectedSearchContext !== props.defaultContext"
      >
        <gat-button
          class="text-soft rounded-full"
          variant="ghost"
          icon="X"
          size="icon"
          @click="[
            clearSearchResults(),
            setSelectedSearchContext(props.defaultContext),
            (searchInput = ''),
            searchInputRef?.focus()
          ]"
        >
        </gat-button>
      </template>
    </gat-text-edit>
    <!-- <search-context-dropdown
      v-if="searchContext"
      class="min-w-24"
      :model-value="selectedSearchContext"
      @update:model-value="
        setSelectedSearchContext($event as SearchContextType)
      "
      input-class="rounded-none border-l-0 rounded-r-sm"
    /> -->
  </div>
</template>
